import React, { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid
} from 'recharts';
import {
  Target, ArrowUpDown, TrendingUp
} from 'lucide-react';

const STATUS_COLORS = {
  active: 'bg-emerald-100 text-emerald-700',
  scheduled: 'bg-blue-100 text-blue-700',
  expired: 'bg-slate-100 text-slate-500',
  draft: 'bg-amber-100 text-amber-700',
};

const BADGE_COLORS = {
  Strong: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  Moderate: 'bg-amber-100 text-amber-700 border-amber-200',
  Weak: 'bg-slate-100 text-slate-500 border-slate-200',
};

const COLUMNS = [
  { key: 'title', label: 'Campaign', align: 'left', sortable: true },
  { key: 'treatment_type', label: 'Treatment', align: 'left', sortable: true },
  { key: 'status', label: 'Status', align: 'center', sortable: true },
  { key: 'leads_count', label: 'Leads', align: 'right', sortable: true },
  { key: 'quotes_count', label: 'Quotes', align: 'right', sortable: true },
  { key: 'bookings_count', label: 'Bookings', align: 'right', sortable: true },
  { key: 'conversion_rate', label: 'Conv %', align: 'right', sortable: true },
  { key: 'redemption_count', label: 'Redeemed', align: 'right', sortable: true },
  { key: 'health_badge', label: 'Health', align: 'center', sortable: true },
];

const fmtMicros = (v) => v != null && v > 0 ? `$${(v / 100).toLocaleString(undefined, { minimumFractionDigits: 2 })}` : '$0.00';

export default function CampaignROIWidget({ data, loading, error }) {
  const [sortKey, setSortKey] = useState('bookings_count');
  const [sortDir, setSortDir] = useState('desc');

  const campaigns = useMemo(() => data?.campaigns || [], [data]);

  const sorted = useMemo(() => {
    if (!campaigns.length) return [];
    const copy = [...campaigns];
    copy.sort((a, b) => {
      const av = a[sortKey] ?? '';
      const bv = b[sortKey] ?? '';
      if (typeof av === 'number' && typeof bv === 'number') {
        return sortDir === 'asc' ? av - bv : bv - av;
      }
      return sortDir === 'asc'
        ? String(av).localeCompare(String(bv))
        : String(bv).localeCompare(String(av));
    });
    return copy;
  }, [campaigns, sortKey, sortDir]);

  const chartData = useMemo(() => {
    if (!sorted.length) return [];
    return sorted
      .filter(c => c.status === 'active' && (c.bookings_count > 0 || c.redemption_count > 0))
      .slice(0, 10)
      .map(c => ({
        name: c.title.length > 16 ? c.title.slice(0, 14) + '\u2026' : c.title,
        commission: c.commission_generated || 0,
        bookings: c.bookings_count,
        redemptions: c.redemption_count,
      }));
  }, [sorted]);

  const handleSort = (key) => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('desc'); }
  };

  const summary = useMemo(() => {
    const active = campaigns.filter(c => c.status === 'active').length;
    const totalRedemptions = campaigns.reduce((s, c) => s + (c.redemption_count || 0), 0);
    const totalBookings = campaigns.reduce((s, c) => s + (c.bookings_count || 0), 0);
    return { total: campaigns.length, active, totalRedemptions, totalBookings };
  }, [campaigns]);

  return (
    <Card className="border border-slate-200/60 shadow-sm" data-testid="campaign-roi-widget">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="text-sm font-semibold text-[#122033] font-[Manrope] flex items-center gap-2">
            <Target className="h-4 w-4 text-emerald-500" />
            Campaign ROI
          </CardTitle>
          <div className="flex gap-2">
            <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-slate-200">
              {summary.total} campaigns
            </Badge>
            <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-emerald-200 text-emerald-600">
              {summary.active} active
            </Badge>
            {!data?.pg_available && (
              <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-amber-200 text-amber-600">
                Revenue: PG offline
              </Badge>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {loading && (
          <div className="space-y-3" data-testid="roi-loading">
            <div className="h-40 bg-slate-50 animate-pulse rounded" />
            {[1,2,3].map(i => <div key={i} className="h-8 bg-slate-50 animate-pulse rounded" />)}
          </div>
        )}

        {!loading && error && (
          <div className="py-6 text-center text-sm text-red-500" data-testid="roi-error">{error}</div>
        )}

        {!loading && !error && campaigns.length === 0 && (
          <div className="py-10 text-center" data-testid="roi-empty">
            <Target className="h-8 w-8 text-slate-200 mx-auto mb-2" />
            <p className="text-sm text-slate-400">No campaigns found</p>
          </div>
        )}

        {!loading && !error && campaigns.length > 0 && (
          <>
            {/* Summary KPIs */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4" data-testid="roi-summary-kpis">
              <div className="bg-slate-50 rounded-md px-3 py-2">
                <p className="text-[10px] text-slate-400 uppercase tracking-wider">Total Campaigns</p>
                <p className="text-lg font-bold text-[#122033] font-[Manrope]">{summary.total}</p>
              </div>
              <div className="bg-emerald-50 rounded-md px-3 py-2">
                <p className="text-[10px] text-emerald-500 uppercase tracking-wider">Active</p>
                <p className="text-lg font-bold text-emerald-700 font-[Manrope]">{summary.active}</p>
              </div>
              <div className="bg-blue-50 rounded-md px-3 py-2">
                <p className="text-[10px] text-blue-500 uppercase tracking-wider">Redemptions</p>
                <p className="text-lg font-bold text-blue-700 font-[Manrope]">{summary.totalRedemptions}</p>
              </div>
              <div className="bg-violet-50 rounded-md px-3 py-2">
                <p className="text-[10px] text-violet-500 uppercase tracking-wider">Bookings</p>
                <p className="text-lg font-bold text-violet-700 font-[Manrope]">{summary.totalBookings}</p>
              </div>
            </div>

            {/* Bar Chart — Active campaigns with activity */}
            {chartData.length > 0 && (
              <div className="mb-5" data-testid="roi-chart">
                <p className="text-[10px] uppercase tracking-wider text-slate-400 mb-2 font-medium">
                  Active Campaign Performance
                </p>
                <ResponsiveContainer width="100%" height={180}>
                  <BarChart data={chartData} barSize={24} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis dataKey="name" tick={{ fontSize: 10, fill: '#64748B' }} axisLine={false} tickLine={false} interval={0} angle={-15} textAnchor="end" height={40} />
                    <YAxis tick={{ fontSize: 10, fill: '#94A3B8' }} axisLine={false} tickLine={false} allowDecimals={false} />
                    <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                    <Bar dataKey="redemptions" fill="#3BA4C5" name="Redemptions" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="bookings" fill="#10B981" name="Bookings" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* Table */}
            <div className="overflow-x-auto" data-testid="roi-table-wrapper">
              <table className="w-full text-sm" data-testid="roi-table">
                <thead>
                  <tr className="border-b border-slate-100">
                    {COLUMNS.map(col => (
                      <th key={col.key}
                        className={`py-2 text-[10px] uppercase text-slate-400 font-medium ${
                          col.align === 'right' ? 'text-right' : col.align === 'center' ? 'text-center' : 'text-left'
                        } ${col.sortable ? 'cursor-pointer select-none hover:text-slate-600' : ''}`}
                        onClick={() => col.sortable && handleSort(col.key)}
                        data-testid={`roi-col-${col.key}`}
                      >
                        <span className="inline-flex items-center gap-0.5">
                          {col.label}
                          {col.sortable && sortKey === col.key && (
                            <ArrowUpDown className="h-2.5 w-2.5 text-emerald-500" />
                          )}
                        </span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sorted.map((row, i) => (
                    <tr key={row.campaign_id} className="border-b border-slate-50 hover:bg-slate-50/50 transition-colors" data-testid={`roi-row-${i}`}>
                      <td className="py-2 text-xs font-medium text-[#122033] max-w-[180px] truncate" title={row.title}>{row.title}</td>
                      <td className="py-2 text-xs text-slate-500 font-mono">{row.treatment_type}</td>
                      <td className="py-2 text-center">
                        <Badge className={`text-[10px] px-1.5 py-0 ${STATUS_COLORS[row.status] || ''}`} data-testid={`roi-status-${i}`}>
                          {row.status}
                        </Badge>
                      </td>
                      <td className="py-2 text-right text-xs text-slate-600">{row.leads_count}</td>
                      <td className="py-2 text-right text-xs text-slate-600">{row.quotes_count}</td>
                      <td className="py-2 text-right text-xs font-semibold text-[#122033]">{row.bookings_count}</td>
                      <td className="py-2 text-right text-xs text-emerald-600">{row.conversion_rate}%</td>
                      <td className="py-2 text-right text-xs text-slate-600">
                        {row.redemption_count}{row.max_redemptions != null ? `/${row.max_redemptions}` : ''}
                      </td>
                      <td className="py-2 text-center">
                        <Badge variant="outline" className={`text-[10px] px-1.5 py-0 ${BADGE_COLORS[row.health_badge] || ''}`} data-testid={`roi-health-${i}`}>
                          {row.health_badge}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="text-[10px] text-slate-300 mt-2 text-right" data-testid="roi-count">
                {campaigns.length} campaign{campaigns.length !== 1 ? 's' : ''}
              </p>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
